import * as React from "react";
import { useCallback, useState, useMemo } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import Icon from "components/common/Icon";
import { widgetId } from "report/report-creation/content/types";
import defaultTitle from "views/components/defaultTitle";
import Popover from "components/common/Popover";
import usePluginEntities from "hooks/usePluginEntities";
import AutoRefreshDisabledProvider from "report/common/AutoRefreshDisabledProvider";
import ReportingWidgetContainer from "report/common/ReportingWidgetContainer";
import AvailableWidgetPreview from "report/common/AvailableWidgetPreview";
import { Row, Column, StyledCheckbox, SmallColumn } from "report/report-creation/content/common";
import Alert from "components/bootstrap/Alert";
import Badge from "components/bootstrap/Badge";
import { Link } from "components/common";
import Routes from "routing/Routes";
import IconButton from "components/common/IconButton";
import usePermissions from "hooks/usePermissions";
const WidgetIcon = () => /* @__PURE__ */ React.createElement(Icon, { name: "analytics", type: "regular" });
const PreviewTitle = styled.h3`
  margin-bottom: 10px;
`;
const PreviewContainer = styled.div`
  max-height: 600px;
  overflow: auto;
`;
export const CheckboxColumn = styled(SmallColumn)`
  text-align: right;
`;
const WidgetPreview = ({ widget, dashboard }) => {
  const { t } = useTranslation("enterprise_report");
  const widgetTypes = usePluginEntities("enterpriseWidgets");
  const widgetType = widgetTypes.find(
    (w) => w.type.toUpperCase() === (widget.type ? widget.type.toUpperCase() : "unknown")
  );
  return widgetType ? /* @__PURE__ */ React.createElement(AutoRefreshDisabledProvider, null, /* @__PURE__ */ React.createElement(ReportingWidgetContainer, null, ({ height, width }) => /* @__PURE__ */ React.createElement(
    AvailableWidgetPreview,
    {
      dashboardId: dashboard.id,
      widgetId: widget.id,
      widget,
      searchId: dashboard.search_id,
      showHeading: false,
      showCaption: false,
      showHandle: false,
      interactive: false,
      limitHeight: false,
      height,
      width,
      hideQuery: true,
      hideDescription: true
    }
  ))) : /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning", onDismiss: () => {
  } }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "widget_row.widget_type_not_found",
      values: { type: widget.type },
      components: { em: /* @__PURE__ */ React.createElement("em", null) }
    }
  ));
};
const StatusBadge = styled(Badge)`
  margin-left: 5px;
  display: inline-flex;
`;
const InReport = () => {
  const { t } = useTranslation("enterprise_report");
  return /* @__PURE__ */ React.createElement(StatusBadge, { bsStyle: "primary" }, t("widget_row.in_report"));
};
const WidgetRow = ({ inReport, widget, dashboard, checked, onCheck, onUncheck }) => {
  var _a;
  const { t } = useTranslation("enterprise_report");
  const ineligibleTitle = t("widget_row.ineligible_title");
  const { eligible } = widget;
  const { isPermitted } = usePermissions();
  const [showPreview, setShowPreview] = useState(false);
  const togglePreview = useCallback(() => {
    if (eligible) {
      setShowPreview((_expanded) => !_expanded);
    }
  }, [eligible]);
  const closePreview = useCallback(() => setShowPreview(false), []);
  const preview = showPreview ? /* @__PURE__ */ React.createElement(WidgetPreview, { widget, dashboard }) : null;
  const id = useMemo(() => widgetId(widget, dashboard), [dashboard, widget]);
  const _onChange = useCallback(
    (e) => e.target.checked ? onCheck(id) : onUncheck(id),
    [id, onCheck, onUncheck]
  );
  const _disabled = inReport || !eligible;
  const widgetTitle = (_a = widget.title) != null ? _a : defaultTitle(widget);
  const title = inReport ? t("widget_row.already_in_report") : !widget.eligible ? ineligibleTitle : t("widget_row.preview_widget", { title: widgetTitle });
  let checkboxTitle = ineligibleTitle;
  if (widget.eligible) {
    checkboxTitle = checked ? t("widget_row.deselect_widget", { title: widgetTitle }) : t("widget_row.select_widget", { title: widgetTitle });
  }
  const isEditPermitted = isPermitted(`view:edit:${dashboard.id}`);
  return /* @__PURE__ */ React.createElement(Row, { $disabled: _disabled, title }, /* @__PURE__ */ React.createElement(SmallColumn, null), /* @__PURE__ */ React.createElement(CheckboxColumn, { $displayCursor: false }, /* @__PURE__ */ React.createElement(StyledCheckbox, { onChange: _onChange, checked, disabled: _disabled, title: checkboxTitle })), /* @__PURE__ */ React.createElement(
    Popover,
    {
      withArrow: true,
      width: 800,
      opened: showPreview,
      closeOnClickOutside: true,
      onClose: closePreview,
      position: "bottom-start"
    },
    /* @__PURE__ */ React.createElement(Popover.Target, null, /* @__PURE__ */ React.createElement(Column, { onClick: togglePreview, $displayCursor: eligible }, /* @__PURE__ */ React.createElement(WidgetIcon, null), " ", widgetTitle, inReport ? /* @__PURE__ */ React.createElement(InReport, null) : null, !widget.eligible ? /* @__PURE__ */ React.createElement(StatusBadge, null, t("widget_row.ineligible_badge")) : null)),
    /* @__PURE__ */ React.createElement(Popover.Dropdown, null, /* @__PURE__ */ React.createElement(PreviewTitle, null, t("widget_row.preview_title", { title: widgetTitle })), /* @__PURE__ */ React.createElement(PreviewContainer, null, preview))
  ), /* @__PURE__ */ React.createElement(Column, null), /* @__PURE__ */ React.createElement(Column, null), isEditPermitted && /* @__PURE__ */ React.createElement(Column, null, /* @__PURE__ */ React.createElement(Link, { to: `${Routes.dashboard_show(dashboard.id)}?focusedId=${widget.id}&editing=true`, target: "_blank" }, /* @__PURE__ */ React.createElement(IconButton, { name: "checkbook", title: t("widget_row.edit_widget") }))));
};
export default WidgetRow;
